import { useEffect, useState } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader } from './Stepper.jsx';
import { listResults } from '../api/results.js';

const RESULT_STATUS_META = {
  pronto: { label: 'Pronto', color: 'var(--success)', bg: 'var(--green-soft)' },
  processando: { label: 'Processando', color: 'var(--warning-dark)', bg: 'var(--overlay-warning-16)' },
};

const CATEGORIES = ['Sangue', 'Urina', 'Fezes', 'Imagem/Cardio'];

// Meus Resultados — busca por nome, ordenação cronológica, filtro por tipo e
// por pedido. Todos os filtros são resolvidos no servidor (GET /results),
// não client-side como no protótipo de design.
export default function ResultsScreen({ onBack, initialOrderId }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent');
  const [category, setCategory] = useState('');
  const [orderFilter, setOrderFilter] = useState(initialOrderId || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      listResults({ q: q || undefined, sort, category: category || undefined, orderId: orderFilter || undefined })
        .then((data) => { if (alive) setResults(data); })
        .catch(() => { if (alive) setError('Não foi possível carregar seus resultados.'); })
        .finally(() => { if (alive) setLoading(false); });
    }, 250); // debounce da busca por texto
    return () => { alive = false; clearTimeout(timer); };
  }, [q, sort, category, orderFilter]);

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <MobHeader title="Meus Resultados" onBack={onBack}/>
      <div style={{ padding: '14px 16px 12px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ position: 'relative' }}>
          <Icon.search size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar pelo nome do exame…" style={{ paddingLeft: 42 }}/>
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 2 }}>
          {[{ k: 'recent', l: 'Mais recentes' }, { k: 'oldest', l: 'Mais antigos' }].map((t) => (
            <button key={t.k} onClick={() => setSort(t.k)} style={{
              padding: '7px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              background: sort === t.k ? 'var(--midnight)' : 'var(--white)',
              color: sort === t.k ? 'var(--white)' : 'var(--text-muted)',
              border: '1px solid ' + (sort === t.k ? 'var(--midnight)' : 'var(--border)'),
              fontFamily: 'var(--font-display)',
            }}>{t.l}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} style={{ fontSize: 13, minHeight: 42 }}>
            <option value="">Todos os tipos</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input" value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)}
            placeholder="PED-XXXXXXX" style={{ fontSize: 13, minHeight: 42 }}/>
        </div>
      </div>

      <div style={{ flex: 1, padding: '14px 16px 24px', overflowY: 'auto' }}>
        {error && (
          <div role="alert" style={{ padding: '10px 14px', background: 'var(--danger-tint)', borderRadius: 10, color: 'var(--danger-strong)', fontSize: 13, marginBottom: 10 }}>
            {error}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-subtle)', fontSize: 13.5 }}>
            Carregando…
          </div>
        ) : (
          <>
            {results.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-subtle)', fontSize: 13.5 }}>
                Nenhum resultado encontrado.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {results.map((r) => {
                const meta = RESULT_STATUS_META[r.status];
                const dt = new Date(r.date);
                const dateStr = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
                return (
                  <div key={r.order_item_id} className="card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--midnight)' }}>{r.exam_name}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>{r.lab_name || 'A confirmar'} · {dateStr}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-subtle)', marginTop: 2 }}>{r.protocol_number} · {r.category || '—'}</div>
                      </div>
                      <span className="chip" style={{ background: meta.bg, color: meta.color, flexShrink: 0 }}>{meta.label}</span>
                    </div>
                    <a
                      href={r.download_url || undefined}
                      target="_blank" rel="noopener noreferrer"
                      aria-disabled={r.status !== 'pronto'}
                      onClick={(e) => { if (r.status !== 'pronto') e.preventDefault(); }}
                      style={{
                        marginTop: 10, width: '100%', padding: '9px 0', borderRadius: 8,
                        background: r.status === 'pronto' ? 'var(--midnight)' : 'var(--marble-soft)',
                        color: r.status === 'pronto' ? 'var(--white)' : 'var(--text-subtle)',
                        fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        textDecoration: 'none', cursor: r.status === 'pronto' ? 'pointer' : 'default',
                      }}
                    >
                      <Icon.download size={15} color={r.status === 'pronto' ? 'var(--white)' : 'var(--text-subtle)'}/> Baixar PDF
                    </a>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
